import React, { FormEvent, useState } from 'react'
import { faCheck, faExternalLink } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import GenericLayout from 'components/GenericLayout'
import { useControlledInput } from 'hooks'
import fetch from 'isomorphic-unfetch'
import Button from 'components/Button'
import MFDAnnouncementBanner from 'components/Collectives/Common/MFDAnnouncementBanner'

function ContactPage() {
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState()
  const [sending, setSending] = useState(false)

  const [firstName, setFirstName, clearFirstName] = useControlledInput('')
  const [lastName, setLastName, clearLastName] = useControlledInput('')
  const [email, setEmail, clearEmail] = useControlledInput('')
  const [title, setTitle, clearTitle] = useControlledInput('')
  const [company, setCompany, clearCompany] = useControlledInput('')
  const [interestedIn, setInterestedIn, clearInterestedIn] =
    useControlledInput('')
  const [additionalInfo, setAdditionalInfo, clearAdditionalInfo] =
    useControlledInput('')

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSending(true)
    const resp = await fetch('/api/contact', {
      method: 'POST',
      body: JSON.stringify({
        firstName,
        lastName,
        email,
        title,
        company,
        interestedIn,
        additionalInfo
      })
    }).then(x => x.json())

    if (resp.success) {
      setSending(false)
      setSuccess(true)
      clearFirstName()
      clearLastName()
      clearEmail()
      clearTitle()
      clearCompany()
      clearInterestedIn()
      clearAdditionalInfo()
    } else {
      setSending(false)
      setSuccess(false)
      setError(
        resp.message ||
          'Failed to submit contact request. Please try again later or email support@upstreamapp.com'
      )
    }
  }

  return (
    <>
      <MFDAnnouncementBanner />
      <GenericLayout bgColor="bg-white" fullWidth>
        <div className="pb-20 lg:px-10 overflow-hidden md:grid md:grid-cols-2">
          <div className="py-16  md:py-10 sm:py-24  lg:max-w-full lg:mx-auto lg:py-0 lg:space-x-40">
            <div className="max-w-full mx-auto sm:max-w-full px-10 md:pl-6 lg:px-10">
              {success ? (
                <div className="mt-9 space-y-3 text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="text-green-600"
                    />
                  </div>
                  <p className="text-lg leading-6 font-medium text-gray-900">{`Awesome! We'll be in touch soon.`}</p>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  className="p-6"
                  style={{ backgroundColor: '#f0f6ff' }}
                >
                  <div className="w-full">
                    <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
                      {`We can't wait to hear from you`}
                    </h2>
                    <p className="mt-4 text-lg text-gray-500 sm:mt-3">
                      {`We'd love to hear from you! Send us a message using the form
                  below, or email us.`}
                    </p>
                  </div>

                  <div className="mt-9 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8 ">
                    <div>
                      <label
                        htmlFor="first_name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        First name
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="first_name"
                          id="first_name"
                          autoComplete="given-name"
                          className="block w-full shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                          value={firstName}
                          onChange={setFirstName}
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="last_name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Last name
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="last_name"
                          id="last_name"
                          autoComplete="family-name"
                          className="block w-full shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                          value={lastName}
                          onChange={setLastName}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Email
                      </label>
                      <div className="mt-1">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          className="block w-full shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                          value={email}
                          onChange={setEmail}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="flex justify-between">
                        <label
                          htmlFor="title"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Title
                        </label>
                        <span
                          id="title_description"
                          className="text-sm text-gray-500"
                        >
                          Optional
                        </span>
                      </div>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="title"
                          id="title"
                          className="block w-full shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                          value={title}
                          onChange={setTitle}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="flex justify-between">
                        <label
                          htmlFor="company"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Company
                        </label>
                        <span
                          id="company_description"
                          className="text-sm text-gray-500"
                        >
                          Optional
                        </span>
                      </div>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="company"
                          id="company"
                          autoComplete="organization"
                          className="block w-full shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                          value={company}
                          onChange={setCompany}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="flex justify-between">
                        <label
                          htmlFor="how_can_we_help"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Which Upstream products are you interested in?
                        </label>
                        <span
                          id="how_can_we_help_description"
                          className="text-sm text-gray-500"
                        >
                          Max. 500 characters
                        </span>
                      </div>
                      <div className="mt-1">
                        <textarea
                          id="how_can_we_help"
                          name="how_can_we_help"
                          aria-describedby="how_can_we_help_description"
                          rows={2}
                          className="block w-full shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                          value={interestedIn}
                          onChange={setInterestedIn}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="flex justify-between">
                        <label
                          htmlFor="additional_info"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {`Anything else you'd like us to know?`}
                        </label>
                        <span
                          id="additional_info_description"
                          className="text-sm text-gray-500"
                        >
                          Max. 500 characters
                        </span>
                      </div>
                      <div className="mt-1">
                        <textarea
                          id="additional_info"
                          name="additional_info"
                          aria-describedby="additional_info_description"
                          rows={4}
                          className="block w-full shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                          value={additionalInfo}
                          onChange={setAdditionalInfo}
                        />
                      </div>
                    </div>

                    <div className="text-right sm:col-span-2">
                      {!!error && (
                        <p className="text-red-500 pt-1 text-sm text-center">
                          {error}
                        </p>
                      )}

                      <Button
                        label={'Submit'}
                        type={'submit'}
                        rounded="lg"
                        size="md"
                        color="blue"
                        loading={sending}
                        disabled={sending}
                        fullWidth
                        className="lg:w-1/2"
                      />
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
          <div className="mx-auto">
            <div className="mx-4 mt-10">
              <img
                className="h-80 w-full object-contain mt-10 md:mt-10 lg:mt-0 lg:top-20 lg:h-96"
                src={'/static/contact/contact.svg'}
                alt="Contact Us"
              />
            </div>
            <div className="text-center py-16 px-4 sm:py-24 sm:px-6 lg:px-8 lg:py-10 my-12 mx-4">
              <h2 className="text-2xl font-extrabold sm:text-4xl tracking-tight">
                <span className="block">Want to email instead?</span>
              </h2>
              <h5>
                <span className="block">{`Send your thoughts & puppy photos.`}</span>
              </h5>

              <p className="mt-2 text-base text-blue hover:tracking-wide transition-all">
                <span>help@upstreamapp.com</span>
                <FontAwesomeIcon
                  icon={faExternalLink}
                  className="ml-3 h-5 w-5 shrink-0"
                  aria-hidden="true"
                />
              </p>
            </div>
          </div>
        </div>
      </GenericLayout>
    </>
  )
}

export default ContactPage
